<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<!-- eslint-disable vue/no-root-v-if -->
<template>
  <aside
    v-if="currentNodeDisplayed"
    class="preview-panel-container"
    :class="{ hidden: !isPreviewPanelVisible }"
    :aria-label="$t('previewPanel')"
  >
    <div class="preview-card">
      <div v-if="currentNodeDisplayed" class="panel-header">
        <PrimeButton
          class="close-btn"
          type="button"
          icon="ri-close-line"
          :aria-label="$t('close')"
          severity="danger"
          rounded
          text
          @click="closeContentPanel"
        />

        <div v-tooltip.top="currentNodeDisplayed.name" class="file-name">
          {{ currentNodeDisplayed.name }}
        </div>
        <div class="nav-actions">
          <PrimeButton
            v-tooltip.bottom="$t(`previousFile`)"
            :aria-label="$t('previousFile')"
            type="button"
            class="nav-btn"
            icon="ri-skip-left-line"
            severity="secondary"
            :disabled="hasOnlyOneFile"
            @click="showPreviousFile(children)"
          />

          <PrimeButton
            v-tooltip.bottom="$t(`nextFile`)"
            :aria-label="$t('nextFile')"
            type="button"
            class="nav-btn"
            icon="ri-skip-right-line"
            severity="secondary"
            icon-pos="right"
            :disabled="hasOnlyOneFile"
            @click="showNextFile(children)"
          />

          <router-link :to="collaboraService.getRoute(currentNodeDisplayed)">
            <PrimeButton
              class="open-btn"
              label="Ouvrir"
              type="button"
              severity="secondary"
              raised
            />
          </router-link>
        </div>
      </div>

      <!-- Content -->
      <div class="preview-content">
        <ImageViewer
          v-if="currentNodeDisplayed && mimeType && mimeType.includes(`image/`)"
          ref="imageViewer"
          :content-node="currentNodeDisplayed"
          :show-title="false"
        />
        <VideoPlayer
          v-else-if="
            currentNodeDisplayed && mimeType && mimeType.includes(`video/`)
          "
          :content-url="url"
          :content-node="currentNodeDisplayed"
        />
        <AudioPlayer
          v-else-if="
            currentNodeDisplayed && mimeType && mimeType.includes(`audio/`)
          "
          :content-url="url"
          :content-node="currentNodeDisplayed"
        />
        <CodeMirrorViewer
          v-else-if="
            currentNodeDisplayed &&
            mimeType &&
            (mimeType.startsWith('text/') ||
              mimeType === 'application/json' ||
              mimeType === 'application/xml' ||
              mimeType === 'application/x-yaml' ||
              mimeType === 'application/x-sh')
          "
          :content-node="currentNodeDisplayed"
        />
        <PDFComponent
          v-else-if="url"
          :key="url"
          ref="pdfComponent"
          :url="url"
          class="pdf-viewer"
        />
      </div>
    </div>
  </aside>
</template>
<script>
import { useNavigationStore } from "@/stores/navigation.js";
import { mapState } from "pinia";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import PDFComponent from "../viewer/PDFComponent.vue";
import ImageViewer from "../viewer/ImageViewer.vue";
import VideoPlayer from "../viewer/VideoPlayer.vue";
import AudioPlayer from "../viewer/AudioPlayer.vue";
import CodeMirrorViewer from "../viewer/CodeMirrorViewer.vue";
import Button from "primevue/button";
import Tooltip from "primevue/tooltip";

export default {
  name: "PreviewPanel",
  components: {
    VideoPlayer,
    ImageViewer,
    AudioPlayer,
    PDFComponent,
    CodeMirrorViewer,
    PrimeButton: Button,
  },
  directives: {
    tooltip: Tooltip,
  },
  inject: ["collaboraService", "isCurrentNodeDisplayed"],
  props: {
    isPreviewPanelVisible: {
      type: Boolean,
      default: false,
    },
    currentNodeDisplayed: {
      type: Object,
      default: () => ({}),
    },
    hasOnlyOneFile: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["closeContentPanel", "showPreviousFile", "showNextFile"],
  data() {
    return {
      url: null,
    };
  },
  computed: {
    ...mapState(useNavigationStore, ["children"]),
  },
  watch: {
    currentNodeDisplayed(newValue) {
      if (newValue != null) {
        this.computeUrl(newValue);
      } else {
        this.closeContentPanel();
      }
    },
  },
  methods: {
    getPDFRef() {
      return this.$refs.pdfComponent;
    },
    getImagePreviewRef() {
      return this.$refs.imageViewer;
    },
    closeContentPanel() {
      this.$emit("closeContentPanel");
    },
    showPreviousFile(nodes) {
      this.$emit("showPreviousFile", nodes);
    },
    showNextFile(nodes) {
      this.$emit("showNextFile", nodes);
    },
    computeUrl(node) {
      this.mimeType = node.content.mimeType;
      this.currentFileName = node.name;
      this.currentFileId = node.id;
      if (
        this.mimeType &&
        (this.mimeType === "application/pdf" ||
          this.mimeType.includes("image/") ||
          this.mimeType.includes("video/") ||
          this.mimeType.includes("audio/"))
      ) {
        this.url = alfrescoFileService.getContentUrl(node.id);
      } else {
        alfrescoFileService
          .getRenditionUrl(node.id)
          .then((url) => {
            this.url = url;
          })
          .catch((error) => {
            if (error.message === "NOT_CREATED") {
              console.warn("Fail loading content : NOT_CREATED");
              return null;
            } else {
              console.error("Fail loading content", error);
            }
          });
      }
    },
  },
};
</script>
<style scoped lang="scss">
.preview-panel-container {
  display: block;
  flex: 1 1 55%;
  border-left: 2px solid var(--p-surface-border);
  padding-left: 1rem;
  min-width: 0;
  overflow: hidden;
}

@media (max-width: 768px) {
  .preview-panel-container {
    flex: 1 1 auto;
    border-left: none;
    border-top: 2px solid var(--p-surface-border);
    padding-left: 0;
    padding-top: 1rem;
  }
}

.preview-card {
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 0.75rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: var(--p-content-background);
  border-bottom: 1px solid var(--p-surface-border);
  gap: 1rem;
}

.close-btn {
  flex-shrink: 0;
}

.file-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.nav-btn {
  // Default button styles
}

.open-btn {
  // Default button styles
}

.preview-content {
  flex: 1;
  overflow: auto;
}

.pdf-viewer {
  height: 100%;
}

.hidden {
  display: none;
}
</style>
